/**
 * Pure perceptual-hash (pHash) comparison helpers shared between the backend
 * (near-duplicate image detection / dedupe) and the shopper-frontend (carousel
 * image deduping).
 *
 * These are intentionally free of any runtime dependencies (no Prisma, no Node
 * APIs) so they can be imported from any package. The Prisma-aware dedupe logic
 * (`dedupeImages`, LSH tables) stays in the backend and imports from here.
 */
/**
 * Match threshold (percentage) for perceptual-hash comparisons.
 *
 * Two images are treated as duplicates when their pHashes are "close enough"
 * according to Hamming distance, converted to a match percentage.
 *
 * NOTE: This is a perceptual match (near-duplicate detection), not a byte-for-byte
 * equality check.
 */
export const MATCH_THRESHOLD = 95;
/** Each hex character of a pHash encodes a 4-bit nibble. */
export const BITS_PER_CHARACTER = 4;
/**
 * Calculates the Hamming distance (bit differences) between two hex-encoded pHashes.
 *
 * Each hex character is interpreted as a 4-bit nibble. The distance is the number
 * of differing bits across the entire hash string.
 *
 * Throws if lengths differ (hashes must be comparable bit-for-bit).
 */
export function calculateHammingDistance(hash1, hash2) {
    if (hash1.length !== hash2.length) {
        throw new Error('Hashes must be of equal length');
    }
    let distance = 0;
    for (let i = 0; i < hash1.length; i++) {
        const hex1 = parseInt(hash1[i], 16);
        const hex2 = parseInt(hash2[i], 16);
        // XOR to find differing bits
        const xor = hex1 ^ hex2;
        // Count set bits using Brian Kernighan's algorithm
        let count = 0;
        let n = xor;
        while (n) {
            count++;
            n &= n - 1;
        }
        distance += count;
    }
    return distance;
}
/**
 * Converts Hamming distance into a match percentage in \([0, 100]\).
 *
 * \(match = (1 - distance / totalBits) * 100\)
 */
export function calculateHashMatchPercentage(hash1, hash2) {
    const distance = calculateHammingDistance(hash1, hash2);
    return (1 - distance / (hash1.length * BITS_PER_CHARACTER)) * 100;
}
/**
 * Returns true when two pHashes are considered a match at the given threshold.
 *
 * Default threshold is `MATCH_THRESHOLD`.
 */
export function isMatch(hash1, hash2, threshold = MATCH_THRESHOLD) {
    return calculateHashMatchPercentage(hash1, hash2) >= threshold;
}
/**
 * Returns true when `hash` matches any candidate at or above `threshold`.
 * Candidates with mismatched length are skipped (different hash sizes are
 * never compared).
 */
export const matchesAnyHashAtThreshold = (hash, candidates, threshold) => candidates.some((candidate) => candidate.length === hash.length &&
    isMatch(hash, candidate, threshold));
export const hasUsablePerceptualHash = (hash) => Boolean(hash && hash.trim().length > 0);
/**
 * Canonicalizes a pHash string into lowercase hex characters.
 *
 * Rationale:
 * - `calculateHammingDistance` uses `parseInt(char, 16)` per nibble. If the string
 *   contains non-hex characters, `parseInt` returns NaN and the later bitwise ops
 *   behave as if that nibble were 0.
 * - This function makes that behavior explicit and consistent by mapping any
 *   invalid character to `'0'` and ensuring all output is lowercase hex.
 *
 * This helps keep comparisons deterministic and bucket keys stable.
 */
export const canonicalizePerceptualHash = (hash) => {
    const HEX = '0123456789abcdef';
    let out = '';
    for (let i = 0; i < hash.length; i++) {
        const nibble = parseInt(hash[i], 16);
        out += HEX[(Number.isNaN(nibble) ? 0 : nibble) & 0xf];
    }
    return out;
};
